import React, { useMemo, useRef } from "react";
import Plot from "react-plotly.js";
import type * as Plotly from "plotly.js";
import { ApiGraphData } from "./SimulationResults";
import { AXIS_TEXT_COLOR, GRID_COLOR, usePlotResize, ZERO_LINE_COLOR } from "./SimulationGraph";
import { AxisSelection, AxisTrace, getAxisTitle, sampleAxes, TIME_AXIS } from "./signalAxes";

export type Axis3D = "x" | "y" | "z";

export const AXES_3D: Axis3D[] = ["x", "y", "z"];

// Time along x, then the first signals along y and z.
export const getDefaultAxes3D = (options: string[]): AxisSelection<Axis3D> => [{
  x: TIME_AXIS,
  y: options[1] ?? TIME_AXIS,
  z: options[2] ?? TIME_AXIS,
}];

// Single-hue sequential ramp (signal blue, light to dark) so the trajectory
// reads from start to end.
const TIME_COLORSCALE: Array<[number, string]> = [[0, "#b9d4f3"], [1, "#12386b"]];
const LINE_WIDTH = 4;

interface SimulationGraph3DProps {
  data: ApiGraphData;
  selection: AxisSelection<Axis3D>;
  traces: AxisTrace<Axis3D>[];
}

const SimulationGraph3D: React.FC<SimulationGraph3DProps> = ({ data, selection, traces }) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  usePlotResize(wrapperRef);

  const plotData = useMemo<Partial<Plotly.PlotData>[]>(() => traces.map((trace) => {
    const { seconds, columns } = sampleAxes(data, AXES_3D.map((axis) => trace.axes[axis]));
    const axisValues = (axis: Axis3D) => columns.get(trace.axes[axis]);
    const showsTime = AXES_3D.some((axis) => trace.axes[axis] === TIME_AXIS);

    // A lone trajectory is shaded by time so it reads from start to end;
    // several need their colour for identity instead.
    const line: Partial<Plotly.ScatterLine> = traces.length === 1
      ? {
        width: LINE_WIDTH,
        color: seconds,
        colorscale: TIME_COLORSCALE,
        showscale: !showsTime,
        colorbar: { title: { text: TIME_AXIS }, thickness: 12, outlinewidth: 0, tickfont: { color: AXIS_TEXT_COLOR } },
      } as Partial<Plotly.ScatterLine>
      : { width: LINE_WIDTH, color: trace.color, dash: trace.dash };

    return {
      type: "scatter3d",
      mode: "lines",
      name: trace.name,
      x: axisValues("x"),
      y: axisValues("y"),
      z: axisValues("z"),
      customdata: seconds,
      line,
      hovertemplate: `${trace.axes.x}: %{x}<br>${trace.axes.y}: %{y}<br>${trace.axes.z}: %{z}<br>t = %{customdata} s<extra></extra>`,
      showlegend: false,
    };
  }), [data, traces]);

  const axisLayout = (title: string): Partial<Plotly.SceneAxis> => ({
    title: { text: title },
    gridcolor: GRID_COLOR,
    zerolinecolor: ZERO_LINE_COLOR,
    linecolor: ZERO_LINE_COLOR,
    backgroundcolor: "#ffffff",
    tickfont: { color: AXIS_TEXT_COLOR },
  });

  return (
    <div ref={wrapperRef} className="cm-graph-wrapper">
      <Plot
        data={plotData}
        layout={{
          autosize: true,
          paper_bgcolor: "#ffffff",
          font: { color: "#121f19", family: "Inter, system-ui, sans-serif" },
          margin: { t: 10, l: 10, r: 10, b: 10 },
          showlegend: false,
          // Keep the camera on data refreshes, reset it when x changes.
          uirevision: getAxisTitle(selection, "x"),
          scene: {
            xaxis: axisLayout(getAxisTitle(selection, "x")),
            yaxis: axisLayout(getAxisTitle(selection, "y")),
            zaxis: axisLayout(getAxisTitle(selection, "z")),
            aspectmode: "cube",
          },
        }}
        config={{ responsive: true }}
        style={{ width: "100%", height: "100%" }}
        useResizeHandler={true}
      />
    </div>
  );
};

export default SimulationGraph3D;
